import React, { useEffect, useState } from "react";
import { X, Info, Clock, Zap } from "lucide-react";
import Button from "./Button";
import { modalSettingsService } from "../../lib/modalSettingsService";

// Separate storage key for the bank notice modal
const STORAGE_KEY = "bank_notice_last_shown";

const BankNoticeModal = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const checkAndShow = async () => {
      try {
        const s = await modalSettingsService.getClientSettings();
        setSettings(s);

        // Admin toggle for bank notice
        if (!s.bank_notice_enabled) return;

        // Only show to affiliates or all
        if (
          !(
            s.notice_modal_target_audience === "affiliates" ||
            s.notice_modal_target_audience === "all"
          )
        ) {
          return;
        }

        const intervalMs =
          (s.bank_notice_show_interval_hours || 6) * 60 * 60 * 1000;
        const last = localStorage.getItem(STORAGE_KEY);
        const now = Date.now();

        if (!last) {
          schedule();
          return;
        }

        const lastTime = parseInt(last, 10);
        if (Number.isFinite(lastTime) && now - lastTime >= intervalMs) {
          schedule();
        }
      } catch (e) {
        // Fallback: do not block UX
      }
    };

    const schedule = () => {
      const t = setTimeout(() => setIsVisible(true), 600);
      return () => clearTimeout(t);
    };

    checkAndShow();
  }, []);

  const handleClose = () => {
    try {
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    } catch {}
    setIsVisible(false);
  };

  if (!isVisible || !settings) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/60 p-4">
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl shadow-black/40 max-w-lg w-full relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#B8460E]/10 rounded-full blur-3xl animate-pulse"></div>
          <div
            className="absolute bottom-0 left-0 w-24 h-24 bg-blue-500/10 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "1s" }}
          ></div>
        </div>

        {/* Shine effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -skew-x-12 translate-x-[-200%] animate-shine pointer-events-none rounded-2xl"></div>

        {/* Close button */}
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors duration-200 z-20"
          onClick={handleClose}
        >
          <X className="h-6 w-6" />
        </button>

        {/* Content */}
        <div className="relative z-10 p-8">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-[#B8460E]/20 rounded-xl">
              <Info className="h-6 w-6 text-[#B8460E]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">
                {settings.bank_notice_title || "Banking Update"}
              </h2>
              {settings.bank_notice_date && (
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <Clock className="h-4 w-4" />
                  <span>{settings.bank_notice_date}</span>
                </div>
              )}
            </div>
          </div>

          {/* Message content */}
          <div className="space-y-4 mb-8">
            {settings.bank_notice_content?.intro && (
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                <p className="text-gray-300 leading-relaxed">
                  {settings.bank_notice_content.intro}
                </p>
              </div>
            )}

            {settings.bank_notice_content?.main && (
              <div className="bg-gradient-to-r from-[#B8460E]/10 to-blue-500/10 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-[#B8460E]/20 rounded-lg mt-1">
                    <Zap className="h-5 w-5 text-[#B8460E]" />
                  </div>
                  <div>
                    <p className="text-gray-300 leading-relaxed">
                      {settings.bank_notice_content.main}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {settings.bank_notice_content?.closing && (
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                <p className="text-gray-300 leading-relaxed">
                  {settings.bank_notice_content.closing}
                </p>
              </div>
            )}
          </div>

          {/* Action button */}
          <div className="flex justify-center">
            <Button
              onClick={handleClose}
              className="bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] hover:from-[#8B3A0E] hover:to-[#B8460E] text-white shadow-lg hover:shadow-xl transition-all duration-300 px-8 py-3 text-lg font-semibold"
            >
              Got it, thanks! 🚀
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankNoticeModal;
